import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import useApi from "../../CustomHooks/useApi";
import { useSelector } from "react-redux";

/* ================= TOGGLE SWITCH ================= */
const ToggleSwitch = ({ enabled, onChange, disabled }) => {
    return (
        <label className="relative inline-flex items-center cursor-pointer">
            <input
                type="checkbox"
                checked={enabled}
                onChange={onChange}
                disabled={disabled}
                className="sr-only peer"
            />
            <div className="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-green-600 
      after:content-[''] after:absolute after:top-0.5 after:left-[2px]
      after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all
      peer-checked:after:translate-x-5"></div>
        </label>
    );
};
/* ================================================= */


const ConfirmModal = ({
    open,
    onClose,
    onConfirm,
    message,
    isPayout,
    aepsLimit,
    setAepsLimit,
    oneTimeLimit,
    setOneTimeLimit,
    limitError
}) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-96 shadow-xl animate-scaleIn">
                <h3 className="text-lg font-bold mb-2">Confirm Action</h3>
                <p className="text-sm text-gray-600 mb-4">{message}</p>

                {/* ✅ AEPS LIMIT INPUT */}
                {isPayout && (
                    <div className="mb-3">
                        <label className="text-sm font-semibold block mb-1">
                            AEPS Settlement Monthly Limit
                        </label>

                        <input
                            type="number"
                            placeholder="Enter Limit (₹)"
                            value={aepsLimit}
                            step="1"
                            min="1000"
                            max="500000"
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                setAepsLimit(value);
                            }}
                            className="border px-3 py-2 rounded w-full"
                        />

                        <div>
                            <label className="text-sm font-semibold block mb-1">
                                AEPS Settlement One-Time Limit
                            </label>

                            <input
                                type="number"
                                placeholder="Enter One-Time Limit (₹)"
                                value={oneTimeLimit}
                                step="1"
                                min="1000"
                                max="500000"
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    setOneTimeLimit(value);
                                }}
                                className="border px-3 py-2 rounded w-full"
                            />
                        </div>

                        {/*ERROR MESSAGE */}
                        {limitError && (
                            <p className="text-red-600 text-xs mt-1">{limitError}</p>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={onConfirm}
                        disabled={isPayout && !aepsLimit}
                        className={`px-4 py-2 rounded text-white 
    ${isPayout && !aepsLimit ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}`}
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};




const MerchantList = () => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [role, setRole] = useState("MERCHANT");

    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [goToPage, setGoToPage] = useState("");
    const [error, setError] = useState("");
    const [serviceError, setServiceError] = useState({});
    const [serviceSuccess, setServiceSuccess] = useState({});
    const [confirmPopup, setConfirmPopup] = useState({
        open: false,
        user: null,
        key: "",
        value: false,
    });
    const [aepsLimit, setAepsLimit] = useState("");
    const [limitError, setLimitError] = useState("");
    const [oneTimeLimit, setOneTimeLimit] = useState("");

    const userId = useSelector((state) => state.auth.employeeDetails.userId);
    const userRole = useSelector((state) => state.auth.employeeDetails.role);


    const hasAccess =
        userRole === "SUPER_ONBOARDING" ||
        (userRole === "COMPLIANCE" &&
            userId === "COMPLIANCE275720252705");

    const isComplianceBlocked =
        userRole === "COMPLIANCE" &&
        userId !== "COMPLIANCE275720252705";




    const size = 10;
    const [isLastPage, setIsLastPage] = useState(false);

    /* APIs */
    const { callApi: fetchUsers } = useApi("/admin/users/all/new/v1/not-of-type");
    const { callApi: searchUser } = useApi("/admin/users/search/new/v1/not-of-type");
    const { callApi: updateServiceApi } = useApi("/admin/users/services/update", "POST");

    const [serviceLoading, setServiceLoading] = useState({});

    /* MASK */
    const maskAadhaar = (a) => (a ? a.replace(/^(\d{4})\d{4}(\d{4})$/, "$1XXXX$2") : "-");
    const maskPan = (p) => (p ? p.replace(/^(.{5}).*(.{1})$/, "$1****$2") : "-");
    const formatDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "-");

    /* FETCH */
    const handleFetch = async (loadMore = false) => {
        setLoading(true);
        try {
            const currentPage = loadMore ? page + 1 : 0;
            let query = `?page=${currentPage}&size=${size}`;
            if (role) query += `&role=${role}`;

            const res = await fetchUsers(null, query);
            const newData = res?.content || [];

            setUsers((prev) => (loadMore ? [...prev, ...newData] : newData));
            setPage(currentPage);
            setIsLastPage(res?.last);
            setTotalPages(res?.totalPages || 0);
            setTotalElements(res?.totalElements || 0);
        } finally {
            setLoading(false);
        }
    };

    /* SEARCH */
    const handleSearch = async () => {
        if (!searchQuery) return;
        setLoading(true);
        const res = await searchUser(null, `?mobileNumber=${searchQuery}`);
        setUsers(res ? [res] : []);
        setTotalElements(res ? 1 : 0);
        setTotalPages(1);
        setLoading(false);
    };

    /* GO PAGE */
    const handleGoToPage = async () => {
        const p = parseInt(goToPage);
        if (isNaN(p) || p < 1 || p > totalPages) {
            setError(`Enter page between 1 and ${totalPages}`);
            return;
        }
        setError("");
        setPage(p - 1);
        handleFetch(false);
    };

    /* SERVICE TOGGLE */
    const handleServiceToggle = async (user, serviceKey, value) => {
        const key = user.username + serviceKey;
        const oldValue = user[serviceKey];

        setServiceLoading((p) => ({ ...p, [key]: true }));
        setServiceError((p) => ({ ...p, [key]: "" }));
        setServiceSuccess((p) => ({ ...p, [key]: "" }));

        try {
            let payload = {
                userName: user.username,
                [serviceKey]: value,
            };

            if (serviceKey === "isPayoutEnabled" && value === true) {
                payload.payoutMonthlyUpperLimit = aepsLimit;
                payload.payoutOneTimeLimit = oneTimeLimit;
            }

            const res = await updateServiceApi(payload);

            // Backend logical error check
            if (res?.success === false || res?.errorCode) {
                throw res;
            }

            // ✅ UPDATE UI ONLY AFTER SUCCESS
            setUsers((prev) =>
                prev.map((u) =>
                    u.username === user.username ? { ...u, [serviceKey]: value } : u
                )
            );

            // Success message
            setServiceSuccess((p) => ({
                ...p,
                [key]: res?.message || "Service updated successfully",
            }));

            setTimeout(() => {
                setServiceSuccess((p) => ({ ...p, [key]: "" }));
            }, 3000);

        } catch (err) {
            console.log("ERROR OBJ:", err);

            const apiMsg =
                err?.message ||
                err?.errorCode ||
                err?.response?.data?.message ||
                "Internal Server Error. Please try again later.";

            // ❌ NO UI CHANGE (toggle stays old)
            setUsers((prev) =>
                prev.map((u) =>
                    u.username === user.username ? { ...u, [serviceKey]: oldValue } : u
                )
            );

            setServiceError((p) => ({
                ...p,
                [key]: apiMsg,
            }));

            setTimeout(() => {
                setServiceError((p) => ({ ...p, [key]: "" }));
            }, 5000);
        } finally {
            setServiceLoading((p) => ({ ...p, [key]: false }));
        }
    };

    const confirmToggle = () => {
        const { user, key, value } = confirmPopup;

        if (key === "isPayoutEnabled" && value) {
            const daily = Number(aepsLimit);
            const oneTime = Number(oneTimeLimit);

            if (!aepsLimit || !oneTimeLimit) {
                setLimitError("Please enter both limits");
                return;
            }

            // AEPS DAILY LIMIT VALIDATION
            if (daily < 1000 || daily > 500000) {
                setLimitError("AEPS Daily Limit must be between ₹1,000 and ₹5,00,000");
                return;
            }

            // ONE TIME LIMIT VALIDATION
            if (oneTime < 1000 || oneTime > 500000) {
                setLimitError("One-Time Limit must be between ₹1,000 and ₹5,00,000");
                return;
            }

            // Optional: One-time limit should not exceed daily limit
            if (oneTime > daily) {
                setLimitError("One-Time Limit cannot be greater than Daily Limit");
                return;
            }
        }

        setLimitError("");

        handleServiceToggle(user, key, value);

        setConfirmPopup({ open: false, user: null, key: "", value: false });
        setAepsLimit("");
        setOneTimeLimit("");
    };



    useEffect(() => {
        if (!searchQuery) {
            handleFetch(false);
        }
    }, [role, searchQuery]);


if (isComplianceBlocked) {
  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
      <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full border border-red-200">

        <div className="text-5xl mb-4">🚫</div>

        <h2 className="text-xl font-bold text-red-600 mb-2">
          Access Denied
        </h2>

        <p className="text-gray-600 text-sm mb-6">
          You don’t have permission to access this panel.
          Please contact admin for access.
        </p>

        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow"
        >
          Refresh
        </button>

      </div>
    </div>
  );
}


  if (!hasAccess) {
  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 to-yellow-100">
      <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full border border-yellow-200">

        <div className="text-5xl mb-4">⚠️</div>

        <h2 className="text-xl font-bold text-yellow-600 mb-2">
          Restricted Access
        </h2>

        <p className="text-gray-600 text-sm mb-6">
          Your role does not have permission to view this page.
        </p>

        <button
          onClick={() => window.history.back()}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow"
        >
          Go Back
        </button>

      </div>
    </div>
  );
}

    return (

        <div className="min-h-screen p-6 bg-[#EEF1F5]">
            <ConfirmModal
                open={confirmPopup.open}
                message={`Are you sure you want to ${confirmPopup.value ? "ENABLE" : "DISABLE"} this service?`}
                isPayout={confirmPopup.key === "isPayoutEnabled" && confirmPopup.value}
                aepsLimit={aepsLimit}
                setAepsLimit={setAepsLimit}
                oneTimeLimit={oneTimeLimit}           // ✅
                setOneTimeLimit={setOneTimeLimit}     // ✅
                limitError={limitError}
                onClose={() => {
                    setConfirmPopup({ open: false, user: null, key: "", value: false });
                    setLimitError("");
                }}
                onConfirm={confirmToggle}
            />




            {/* ================= FILTER BAR (KEEPED) ================= */}
            <div className="bg-white p-4 rounded-2xl shadow-lg mb-4 flex flex-wrap gap-3 items-center border">

                {/* STATS */}
                <div className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-semibold border border-indigo-100">
                    Total: {totalElements}
                </div>

                <div className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-semibold border border-emerald-100">
                    Pages: {totalPages}
                </div>

                <div className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-semibold border border-amber-100">
                    Page: {page + 1}
                </div>

                {/* GO TO PAGE */}
                <input
                    type="number"
                    placeholder="Go Page"
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    className="border border-gray-200 px-3 py-2 rounded-lg w-24 focus:ring-2 focus:ring-indigo-400 outline-none text-sm"
                />

                <button
                    onClick={handleGoToPage}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md transition"
                >
                    Go
                </button>

                {/* SEARCH */}
                <input
                    type="text"
                    placeholder="Search Mobile"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="border border-gray-200 px-3 py-2 rounded-lg w-64 focus:ring-2 focus:ring-indigo-400 outline-none text-sm"
                />

                <button
                    onClick={handleSearch}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md transition"
                >
                    Search
                </button>

                {/* ROLE FILTER */}
                <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="border border-gray-200 px-3 py-2 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-400 outline-none"
                >
                    <option value="">ALL ROLES</option>
                    <option value="MASTER_DISTRIBUTOR">MASTER DISTRIBUTOR</option>
                    <option value="DISTRIBUTOR">DISTRIBUTOR</option>
                    <option value="MERCHANT">MERCHANT</option>
                </select>

            </div>


            {error && <p className="text-red-600">{error}</p>}
            {/* ================= MERCHANT CARDS GRID ================= */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

                {users.map((u, i) => (
                    <motion.div
                        key={i}
                        className="bg-white p-4 rounded-xl shadow hover:shadow-lg border border-gray-200"
                        whileHover={{ scale: 1.02 }}
                    >

                        {/* HEADER */}
                        <div className="flex justify-between items-center border-b pb-2 mb-2">
                            <div>
                                <h3 className="font-bold text-lg text-indigo-700">{u.mobileNumber}</h3>
                                <p className="text-xs text-gray-500">User ID: {u.userId}</p>
                                <p className="text-xs font-semibold text-green-600">{u.role}</p>
                            </div>

                            <span className={`px-2 py-1 text-xs font-bold rounded 
                ${u.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                {u.status}
                            </span>
                        </div>

                        {/* BASIC INFO */}
                        <div className="text-xs space-y-1">
                            <p><b>Full Name:</b> {u.fullName}</p>
                            <p><b>Shop:</b> {u.shopName}</p>
                            <p><b>Shop Address:</b> {u.shopAddress}</p>
                            <p><b>Shop State:</b> {u.shopState}</p>
                            <p><b>Shop District:</b> {u.shopDistrict}</p>
                            <p><b>Shop Pin Code:</b> {u.shopPinCode}</p>
                            <p><b>Mobile:</b> {u.mobileNumber}</p>
                            <p><b>Email:</b> {u.email}</p>
                            <p><b>MID:</b> {u.mid}</p>
                            <p><b>Merchant A/C:</b> {u.merchantAccountNo}</p>
                        </div>

                        {/* IDENTITY */}
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                            <p><b>PAN:</b> {maskPan(u.pan)}</p>
                            <p><b>Aadhaar:</b> {maskAadhaar(u.aadhaar)}</p>
                        </div>

                        {/* BANK */}
                        <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                            <p><b>Bank A/C:</b> {u.bankAccountNo}</p>
                            <p><b>IFSC:</b> {u.bankIfsc}</p>
                        </div>

                        {/* CREATED */}
                        <p className="text-xs text-gray-500 mt-2">
                            Created: {formatDate(u.createdAt)}
                        </p>
                        {/* SERVICES TOGGLES */}
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-semibold">
                            {[
                                ["isDmtEnabled", "DMT"],
                                ["isAepsEnabled", "AEPS"],
                                ["isAepsEazyPayEnabled", "AEPS EZYPAY"],
                                ["isRechargeEnabled", "RECHARGE"],
                                ["isPayoutEnabled", "PAYOUT"],
                                ["isBbpsEnabled", "BBPS"],
                                ["isPpiEnabled", "PPI"],
                                ["isFastagEnabled", "FASTAG"],
                                ["isPgInEnabled", "PAYMENT GATEWAY"],
                                ["isCcbpEnabled", "CREDIT CARD BILL PAYMENT"],
                                ["isEducationEnabled", "EDUCATION"],
                                ["isElectricityBillPayEnabled", "ELECTRICITY BILL PAYMENT"],
                            ].map(([key, label]) => (
                                <div key={key} className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                                    <span>{label}</span>
                                    <ToggleSwitch
                                        enabled={u[key]}
                                        disabled={serviceLoading[u.username + key]}
                                        onChange={(e) => {
                                            setConfirmPopup({
                                                open: true,
                                                user: u,
                                                key,
                                                value: e.target.checked,
                                            });
                                        }}
                                    />


                                </div>
                            ))}
                        </div>
                        {/* SUCCESS MESSAGE */}
                        {Object.keys(serviceSuccess)
                            .filter((k) => k.startsWith(u.username))
                            .map((k) =>
                                serviceSuccess[k] ? (
                                    <div key={k} className="mt-2 text-xs bg-green-100 text-green-700 p-2 rounded border border-green-300">
                                        ✅ {serviceSuccess[k]}
                                    </div>
                                ) : null
                            )}

                        {/* ERROR MESSAGE */}
                        {Object.keys(serviceError)
                            .filter((k) => k.startsWith(u.username))
                            .map((k) =>
                                serviceError[k] ? (
                                    <div key={k} className="mt-2 text-xs bg-red-100 text-red-700 p-2 rounded border border-red-300">
                                        ❌ {serviceError[k]}
                                    </div>
                                ) : null
                            )}



                    </motion.div>
                ))}

            </div>

            {/* ================= LOAD MORE BUTTON ================= */}
            {!isLastPage && (
                <div className="text-center mt-6">
                    <button
                        onClick={() => handleFetch(true)}
                        disabled={loading}
                        className="bg-gray-800 hover:bg-black text-white px-6 py-2 rounded-lg shadow"
                    >
                        {loading ? "Loading..." : "Load More"}
                    </button>
                </div>
            )}

        </div>
    );
};

export default MerchantList;
